import { FormEvent, useCallback, useEffect, useState } from 'react';
import { issueFor, ValidationIssue } from '../../services/deliveryConfigApi';
import { promotionsApi, toPromoIssues } from '../../services/promotionsApi';
import { PromoSettings } from '../../types/promotions';
import { ToastView, useToast } from '../promotions/useToast';

const CAP_MESSAGE = 'Maximum discount must be between 1% and 100%.';
const MUTED = { fontSize: '0.75rem', color: 'var(--admin-text-muted)' };

/**
 * Spec 069 — the resolved `updatedByName` wins; a raw subject id is never shown in full (it falls
 * back to "Admin · <last 8>", the ActorChip rule); older payloads may carry a name in `updatedBy`.
 */
function actorLabel(updatedBy: string | null, updatedByName?: string | null): string | null {
  if (updatedByName?.trim()) return updatedByName.trim();
  if (!updatedBy) return null;
  return /^[0-9a-f-]{36}$/i.test(updatedBy) ? `Admin · ${updatedBy.slice(-8)}` : updatedBy;
}

/**
 * Spec 059 Flow E — the global maximum discount (D-4, BR-11), on Finance → Configuration.
 * Lowering it below enabled codes asks first and lists them (AC-29); every discount computed from
 * then on is bounded by the new cap, bookings already made keep their frozen discount.
 */
export default function PromotionsCapCard({ token }: { token: string }) {
  const [settings, setSettings] = useState<PromoSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [value, setValue] = useState('');
  const [issues, setIssues] = useState<ValidationIssue[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const { toast, show } = useToast();
  const [confirming, setConfirming] = useState<{ cap: number; codes: PromoSettings['codesAboveCap'] } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const s = await promotionsApi.getSettings(token);
      setSettings(s);
      setValue(String(s.maxDiscountPercent));
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load the promotions settings');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const save = async (cap: number) => {
    setConfirming(null);
    setSaving(true);
    try {
      const next = await promotionsApi.updateSettings(cap, token);
      setSettings(next);
      setValue(String(next.maxDiscountPercent));
      show(`Saved. Maximum discount is now ${next.maxDiscountPercent}%.`);
    } catch (err) {
      const parsed = toPromoIssues(err);
      if (parsed.length > 0) setIssues(parsed.map((i) => ({ field: 'maxDiscountPercent', message: i.message })));
      else setError(err instanceof Error ? err.message : 'Failed to save the maximum discount');
    } finally {
      setSaving(false);
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (saving || !settings) return;
    setError(null);
    const cap = Number(value.trim());
    if (value.trim() === '' || !Number.isInteger(cap) || cap < 1 || cap > 100) {
      setIssues([{ field: 'maxDiscountPercent', message: CAP_MESSAGE }]);
      return;
    }
    setIssues([]);
    const affected = (settings.codesAboveCap ?? []).filter((c) => c.discountValue > cap);
    if (cap < settings.maxDiscountPercent && affected.length > 0) { setConfirming({ cap, codes: affected }); return; }
    save(cap);
  };

  const fieldError = issueFor(issues, 'maxDiscountPercent');
  const actor = actorLabel(settings?.updatedBy ?? null, settings?.updatedByName);

  return (
    <div className="admin-card" style={{ marginTop: '1rem' }} data-testid="promotions-cap-card">
      <div className="admin-card-header">
        <h3 className="admin-card-title">Promotions</h3>
        {settings?.updatedAt && (
          <span style={MUTED} data-testid="cap-last-changed">
            Last changed {new Date(settings.updatedAt).toLocaleString()}{actor ? ` by ${actor}` : ''}
          </span>
        )}
      </div>
      {loading ? (
        <div className="admin-loading"><div className="admin-loading-spinner" /></div>
      ) : loadError || !settings ? (
        <div>
          <div className="admin-info-banner danger" role="alert" data-testid="cap-load-error">{loadError ?? 'Settings unavailable'}</div>
          <button type="button" className="admin-btn admin-btn-sm admin-btn-outline" onClick={load}>Try again</button>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: '1rem' }}>
            <div style={MUTED}>Maximum discount in force</div>
            <div style={{ fontSize: '1.125rem', fontWeight: 600 }} data-testid="cap-in-force">{settings.maxDiscountPercent}%</div>
            <div style={MUTED}>Bounds every promo code — percentage and fixed — on new bookings. Garagat funds the discount; garage payouts never change.</div>
          </div>
          {error && <div className="admin-info-banner danger" role="alert">{error}</div>}
          <form onSubmit={submit} aria-label="Maximum promo discount" noValidate style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div className="admin-field" style={{ margin: 0 }}>
              <label className="admin-field-label" htmlFor="promo-cap">Maximum discount (%)</label>
              <input id="promo-cap" type="number" step={1} min={1} max={100} inputMode="numeric" className="admin-search-input" style={{ width: '10rem' }}
                value={value} disabled={saving} aria-invalid={!!fieldError} aria-describedby={fieldError ? 'promo-cap-error' : undefined}
                onChange={(e) => setValue(e.target.value)} />
              {fieldError && <div id="promo-cap-error" role="alert" style={{ fontSize: '0.75rem', color: 'var(--admin-danger)', marginTop: '0.25rem' }}>{fieldError}</div>}
            </div>
            <button type="submit" className="admin-btn admin-btn-sm admin-btn-primary" style={{ marginTop: '1.6rem' }} disabled={saving}>
              {saving ? 'Saving…' : 'Save maximum'}
            </button>
          </form>
        </>
      )}
      {confirming && (
        <div className="admin-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="cap-confirm-title">
          <div className="admin-modal admin-modal-sm">
            <div className="admin-modal-header"><h3 id="cap-confirm-title">Lower the maximum to {confirming.cap}%?</h3></div>
            <div className="admin-modal-body">
              <p>These enabled codes are above the new maximum. From now on they give at most {confirming.cap}%; bookings already made keep their discount.</p>
              <ul data-testid="cap-affected-codes">
                {confirming.codes.map((c) => <li key={c.id}><strong>{c.code}</strong> — {c.discountValue}%</li>)}
              </ul>
            </div>
            <div className="admin-modal-footer">
              <button type="button" className="admin-btn" onClick={() => setConfirming(null)}>Cancel</button>
              <button type="button" className="admin-btn admin-btn-primary" onClick={() => save(confirming.cap)}>Lower to {confirming.cap}%</button>
            </div>
          </div>
        </div>
      )}
      <ToastView toast={toast} />
    </div>
  );
}
